import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Sidebar from '../../../components/Sidebar';
import { HiMenu, HiChevronDown, HiPencil, HiTrash, HiChevronLeft, HiChevronRight, HiArrowLeft, HiPlus, HiX, HiSearch, HiStar, HiUpload } from 'react-icons/hi';
import Toast from '../../../components/toaster';
import {
  listPracticeQuiz,
  createPracticeQuiz as createQuizQuestion,
  updatePracticeQuiz as updateQuizQuestion,
  deletePracticeQuiz as deleteQuizQuestion,
  uploadPracticeJson,
  getPracticeQuiz,
} from '../../../services/practiceService';
import { uploadImage as uploadAssetImage } from '../../../services/assetService';
import {
  DndContext,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";

const quizCache = {
  quizzes: null,
  contentId: null,
  page: 1,
  totalPages: 1,
  totalCount: 0,
  firstLoad: true,
};


const SortableReorderRow = ({ id, index, value, onChange, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-2">
      <span
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 flex-shrink-0 touch-none select-none"
        title="Drag to reorder"
      >
        ⠿
      </span>
      <span className="text-xs font-medium text-gray-500 w-5 text-right flex-shrink-0">{index + 1}.</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none"
        placeholder={`Item ${index + 1}`}
      />
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="p-2 text-brand hover:bg-brand/10 rounded-lg transition-colors flex-shrink-0"
        >
          <HiX className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

const Quiz = () => {
  const { moduleId, unitId, lessonId } = useParams();
  const contentId = lessonId;
  const chapterId = unitId;
  const navigate = useNavigate();
  const location = useLocation();
  const fromName = location.state?.fromName; // Quiz header: content card name
  const unitName = location.state?.unitName; // Used to go back to LessonContent header
  const moduleName = location.state?.moduleName;
  const chapterName = location.state?.chapterName;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const [quizzes, setQuizzes] = useState(quizCache.contentId === contentId ? (quizCache.quizzes || []) : []);
  const [isLoading, setIsLoading] = useState(false);
  const [hasAnyQuizzes, setHasAnyQuizzes] = useState(true);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [quizToDelete, setQuizToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showReorderModal, setShowReorderModal] = useState(false);
  const [reorderQuizzes, setReorderQuizzes] = useState([]);
  const [reorderSaveLoading, setReorderSaveLoading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadingCsv, setUploadingCsv] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showUploadResultModal, setShowUploadResultModal] = useState(false);
  const [openSection, setOpenSection] = useState(null);
  const [uploadResult, setUploadResult] = useState({
    successful: [],
    skipped: [],
    error: [],
  });
  const [paginationLoading, setPaginationLoading] = useState(false);

  const [imageUploading, setImageUploading] = useState(false);

  const [newQuiz, setNewQuiz] = useState({
    content_id: '',
    question_type: 'text_text',
    question_text: '',
    question_image_url: '',
    difficulty_level: 'medium',
    options: [
      { id: 1, text: '', is_correct: false },
      { id: 2, text: '', is_correct: false },
    ],
    correct_answer_index: 0,
    match_pairs: [
      { id: 1, left_item: '', right_item: '', order: 0 },
    ],
    reorder_correct_order: [{ id: 1, text: '' }, { id: 2, text: '' }],
    fill_blank_correct_order: [
      { id: 1, title: '', isBlank: false },
    ],
    explanation: '',
    points: 5,
    coins: 0,
    question_audio_url: '',
    order: 1,
  });

  const [editingQuiz, setEditingQuiz] = useState({
    question_id: null,
    content_id: '',
    question_type: 'text_text',
    question_text: '',
    question_image_url: '',
    difficulty_level: 'medium',
    options: [
      { id: 1, text: '', is_correct: false },
      { id: 2, text: '', is_correct: false },
    ],
    correct_answer_index: 0,
    match_pairs: [
      { id: 1, left_item: '', right_item: '', order: 0 },
    ],
    reorder_correct_order: [{ id: 1, text: '' }, { id: 2, text: '' }],
    fill_blank_correct_order: [
      { id: 1, title: '', isBlank: false },
    ],
    explanation: '',
    points: '',
    order: 1,
    is_active: true,
  });

  const [addErrors, setAddErrors] = useState({
    question_text: '',
    options: '',
    correct_answer: '',
    points: '',
    reorder_correct_order: '',
    fill_blank_correct_order: '',
  });


  const [editErrors, setEditErrors] = useState({
    points: '',
    reorder_correct_order: '',
    fill_blank_correct_order: '',
  });


  const [error, setError] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null); // 'addCorrect', 'addDifficulty', 'editCorrect', 'editDifficulty'

  const [page, setPage] = useState(quizCache.page || 1);
  const [limit] = useState(16);
  const [totalPages, setTotalPages] = useState(quizCache.totalPages || 1);
  const [totalCount, setTotalCount] = useState(quizCache.totalCount || 0);

  // Debounce search query
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  const searchDebounceTimerRef = useRef(null);
  const paginationDelayTimerRef = useRef(null);
  const paginationInitialSkipRef = useRef(true);

  const getPaginationRange = () => {
    if (totalPages === 0) return [];

    const totalPageNumbers = 5;
    const totalBlocks = totalPageNumbers + 2;

    if (totalPages <= totalBlocks) {
      return Array.from({ length: totalPages }, (_, idx) => idx + 1);
    }

    const leftSiblingIndex = Math.max(page - 1, 1);
    const rightSiblingIndex = Math.min(page + 1, totalPages);
    const shouldShowLeftDots = leftSiblingIndex > 2;
    const shouldShowRightDots = rightSiblingIndex < totalPages - 1;

    const firstPageIndex = 1;
    const lastPageIndex = totalPages;
    const pages = [firstPageIndex];

    const startPage = shouldShowLeftDots ? leftSiblingIndex : 2;
    const endPage = shouldShowRightDots ? rightSiblingIndex : totalPages - 1;

    if (shouldShowLeftDots) {
      pages.push('dots');
    }

    for (let pageNum = startPage; pageNum <= endPage; pageNum += 1) {
      pages.push(pageNum);
    }

    if (shouldShowRightDots) {
      pages.push('dots');
    }

    pages.push(lastPageIndex);
    return pages;
  };

  const normalizeQuizItem = (item, index) => {
    const questionId =
      item.question_id || item.id || item._id || `quiz-${index}`;

    const questionType = item.question_type || 'text_text';
    let optionsArray = [];
    let optionsMap = {};
    let correctAnswerText = '';
    let correctAnswer = 'A';
    let matchPairs = [];

    const rawPairs = Array.isArray(item.match_pairs) && item.match_pairs.length > 0
      ? item.match_pairs
      : item.options;
    if (questionType === 'match_pairs' && Array.isArray(rawPairs) && rawPairs.length > 0) {
      matchPairs = rawPairs
        .filter(opt => opt.is_active !== false)
        .sort((a, b) => (a.order || 0) - (b.order || 0));

      // For match_pairs, we don't have a single correct answer, so show all pairs
      correctAnswerText = 'Match the pairs';
    } else if (Array.isArray(item.options) && item.options.length > 0) {
      // Text_text format: options array with option_text and is_correct
      optionsArray = item.options
        .filter(opt => opt.is_active !== false)
        .sort((a, b) => (a.order || 0) - (b.order || 0));

      const correctOption = optionsArray.find(opt => opt.is_correct === true);
      const correctIndex = correctOption ? optionsArray.indexOf(correctOption) : 0;

      // Build options map for display (A, B, C, D, etc.)
      optionsArray.forEach((opt, idx) => {
        const label = String.fromCharCode(65 + idx); // A, B, C, D, etc.
        optionsMap[label] = opt.option_text || opt.text || '';
      });

      if (correctOption) {
        const correctLabel = String.fromCharCode(65 + correctIndex);
        correctAnswer = correctLabel;
        correctAnswerText = `Option ${correctLabel} - ${correctOption.option_text || correctOption.text || ''}`;
      }
    } else {
      // Legacy format: individual option fields
      const optionA = item.option_a_text || item.optionA || item.options?.A || '';
      const optionB = item.option_b_text || item.optionB || item.options?.B || '';
      const optionC = item.option_c_text || item.optionC || item.options?.C || '';
      const optionD = item.option_d_text || item.optionD || item.options?.D || '';

      correctAnswer = item.correct_answer || 'A';
      optionsMap = {
        A: optionA,
        B: optionB,
        C: optionC,
        D: optionD,
      };
      const correctOptionText = optionsMap[correctAnswer] || '';
      correctAnswerText = item.correct_answer_text ||
        (correctOptionText ? `Option ${correctAnswer} - ${correctOptionText}` : '');
    }

    const lessonName = item.lesson?.title || item.lesson_name || '';

    return {
      ...item,
      id: questionId,
      question_id: questionId,
      question_text: item.question_text || item.question || '',
      question: item.question_text || item.question || '',
      question_type: questionType,
      options: optionsArray.length > 0 ? optionsArray : (matchPairs.length > 0 ? matchPairs : optionsMap),
      match_pairs: matchPairs,
      optionsMap: optionsMap,
      correct_answer: correctAnswer,
      correct_answer_text: correctAnswerText,
      lesson_name: lessonName,
      points: item.points ?? item.points ?? 0,
      difficulty_level: ['easy', 'medium', 'hard'].includes(item.difficulty_level) ? item.difficulty_level : 'medium',
      is_active:
        typeof item.is_active === 'boolean' ? item.is_active : true,
      status:
        typeof item.is_active === 'boolean'
          ? item.is_active
            ? 'Active'
            : 'Inactive'
          : item.status || 'Active',
    };
  };

  const extractQuizItems = (response) => {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.data?.data)) return response.data.data;
    if (Array.isArray(response?.data?.docs)) return response.data.docs;
    if (Array.isArray(response?.data?.list)) return response.data.list;
    if (Array.isArray(response?.result)) return response.result;
    if (Array.isArray(response?.list)) return response.list;
    return [];
  };

  const loadQuizzes = async (currentPage = page, currentSearch = debouncedSearch) => {
    const isFirstLoad = quizCache.firstLoad || quizCache.contentId !== contentId;
    const isSearchChange = currentSearch !== (quizCache.lastSearch || '');
    const isPageChange = currentPage !== (quizCache.page || 1);

    if (isFirstLoad || isSearchChange || isPageChange) {
      setIsLoading(true);
    }
    setError(null);
    try {
      // Filter by contentId if provided in route params, and only show active quizzes
      const response = await listPracticeQuiz(currentPage, limit, currentSearch, contentId);

      // Set loading to false immediately after API resolves
      setIsLoading(false);

      const items = extractQuizItems(response);

      const dataToUse = items;

      let total = 0;
      let totalP = 0;

      // Attempt to find total counts from response
      if (response?.pagination?.totalCount) {
        // New API format with pagination object
        total = response.pagination.totalCount;
        totalP = response.pagination.nextPage > 0 ? response.pagination.nextPage : Math.ceil(total / response.pagination.pageSize || limit);
      } else if (response?.total || response?.data?.total) {
        total = response.total || response.data.total;
        totalP = response.totalPages || response.data.totalPages || Math.ceil(total / limit);
      } else {
        total = items.length;
        totalP = Math.ceil(total / limit);
      }

      // Hybrid pagination handle
      let processedItems = dataToUse;
      if (processedItems.length > limit) {
        const startIndex = (currentPage - 1) * limit;
        const endIndex = startIndex + limit;
        processedItems = processedItems.slice(startIndex, endIndex);
      }

      const normalized = processedItems.map((item, index) =>
        normalizeQuizItem(item, index)
      );
      setQuizzes(normalized);
      setTotalCount(total);
      setTotalPages(totalP);

      if (!currentSearch) {
        setHasAnyQuizzes(total > 0 || normalized.length > 0);
      } else if (total > 0 || normalized.length > 0) {
        setHasAnyQuizzes(true);
      }

      quizCache.quizzes = normalized;
      quizCache.contentId = contentId;
      quizCache.page = currentPage;
      quizCache.totalPages = totalP;
      quizCache.totalCount = total;
      quizCache.lastSearch = currentSearch;
      quizCache.firstLoad = false;
    } catch (err) {
      // console.error(err);
      setError(err.message || 'Failed to load quizzes');
      setQuizzes([]);
      setTotalCount(0);
      setTotalPages(0);
      setIsLoading(false);
      quizCache.firstLoad = false;
    } finally {
      // Guarantee loader stops even if there's an unexpected error
      setIsLoading(false);
    }
  };

  // Debounce search query
  useEffect(() => {
    // Clear existing timer
    if (searchDebounceTimerRef.current) {
      clearTimeout(searchDebounceTimerRef.current);
    }

    searchDebounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      // Reset to first page when search changes
      setPage((currentPage) => currentPage !== 1 ? 1 : currentPage);
    }, 500); // 500ms debounce

    return () => {
      if (searchDebounceTimerRef.current) {
        clearTimeout(searchDebounceTimerRef.current);
      }
    };
  }, [searchQuery]);

  useEffect(() => {
    if (paginationInitialSkipRef.current) {
      paginationInitialSkipRef.current = false;
      return;
    }
    if (paginationDelayTimerRef.current) {
      clearTimeout(paginationDelayTimerRef.current);
      paginationDelayTimerRef.current = null;
    }
    setPaginationLoading(true);
    paginationDelayTimerRef.current = setTimeout(() => {
      setPaginationLoading(false);
      paginationDelayTimerRef.current = null;
    }, 200);
    return () => {
      if (paginationDelayTimerRef.current) {
        clearTimeout(paginationDelayTimerRef.current);
        paginationDelayTimerRef.current = null;
      }
    };
  }, [page]);

  useEffect(() => {
    const hasCache = quizCache.quizzes !== null && quizCache.contentId === contentId && !quizCache.firstLoad && quizCache.page === page && debouncedSearch === (quizCache.lastSearch || '');

    if (hasCache && contentId) {
      setQuizzes(quizCache.quizzes);
      setPage(quizCache.page);
      setTotalPages(quizCache.totalPages);
      setTotalCount(quizCache.totalCount);
    }

    // Keep cached data for fast UI paint, but always sync with backend to avoid stale data.
    loadQuizzes(page, debouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, contentId, debouncedSearch]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        activeDropdown &&
        !event.target.closest('.custom-dropdown-container')
      ) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeDropdown]);


  const handleDelete = (quizId) => {
    const quiz = quizzes.find(
      (q) => q.id === quizId || q.question_id === quizId
    );
    setQuizToDelete(quiz || null);
    setShowDeleteModal(!!quiz);
  };

  const confirmDelete = async () => {
    if (!quizToDelete) {
      setShowDeleteModal(false);
      return;
    }
    setDeleting(true);
    try {
      const questionId = quizToDelete.question_id || quizToDelete.id;
      await deleteQuizQuestion(questionId);
      setShowDeleteModal(false);
      setQuizToDelete(null);
      setIsLoading(true);
      await loadQuizzes();
    } catch (err) {
      Toast.error(err.message || 'Failed to delete quiz');
    } finally {
      setDeleting(false);
      setIsLoading(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setQuizToDelete(null);
  };

  const handleSaveQuizOrder = async () => {
    // Validate contentId is present
    if (!contentId) {
      Toast.error('Content ID is missing. Cannot save quiz order.');
      return;
    }

    setReorderSaveLoading(true);
    try {
      const payload = reorderQuizzes.map((q, index) => ({
        question_id: String(q.id || q.question_id || q._id),
        order: index + 1,
      }));

      // Update each quiz's order via API
      const updatePromises = payload.map(async (item) => {
        const quiz = reorderQuizzes.find(
          (q) => String(q.id || q.question_id || q._id) === item.question_id
        );
        if (quiz) {
          return updateQuizQuestion({
            question_id: item.question_id,
            content_id: quiz.content_id || contentId,
            question_type: quiz.question_type || 'text_text',
            question_text: quiz.question_text || quiz.question || '',
            order: item.order,
            points: quiz.points || quiz.points || 0,
            is_active: quiz.is_active !== undefined ? quiz.is_active : true,
            explanation: quiz.explanation || '',
            options: quiz.options || [],
            match_pairs: quiz.match_pairs || [],
          });
        }
      });

      await Promise.all(updatePromises);
      setShowReorderModal(false);
      Toast.success('Quiz order updated successfully');
      setIsLoading(true);
      await loadQuizzes();
    } catch (err) {
      //  console.error(err);
      Toast.error('Failed to save quiz order');
    } finally {
      setReorderSaveLoading(false);
      setIsLoading(false);
    }
  };


  // Custom Grid Icon Component (3 rows, 2 dots each - vertical layout)
  const GridIcon = ({ size = 18 }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="text-gray-400"
    >
      {/* Left column - 3 dots */}
      <circle cx="4.5" cy="3" r="1.5" fill="currentColor" />
      <circle cx="4.5" cy="9" r="1.5" fill="currentColor" />
      <circle cx="4.5" cy="15" r="1.5" fill="currentColor" />
      {/* Right column - 3 dots */}
      <circle cx="13.5" cy="3" r="1.5" fill="currentColor" />
      <circle cx="13.5" cy="9" r="1.5" fill="currentColor" />
      <circle cx="13.5" cy="15" r="1.5" fill="currentColor" />
    </svg>
  );

  const SortableQuizItem = ({ quiz, index }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
    } = useSortable({
      id: quiz.id || quiz.question_id || quiz._id,
    });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    return (
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        className="flex items-center justify-between p-4 bg-[#FDF6EF] rounded-xl border border-white/10 cursor-grab active:cursor-grabbing touch-none"
      >
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <div className="text-black flex items-center justify-center text-sm font-semibold min-w-[20px]">
            {index + 1}
          </div>

          {/* <div className="w-10 h-10 rounded-lg bg-brand text-black flex items-center justify-center font-bold flex-shrink-0">
            Q
          </div> */}

          <p className="text-black font-medium flex-1  min-w-0 break-words line-clamp-2 overflow-hidden "
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}>
            {quiz.question_text || quiz.question || 'Untitled Quiz'}

          </p>
        </div>

        <div className="flex-shrink-0 ml-2">
          <GridIcon size={18} />
        </div>
      </div>
    );
  };

  const handleEdit = async (quizId) => {
    let quiz = quizzes.find(
      (q) => q.id === quizId || q.question_id === quizId || q._id === quizId
    );
    if (!quiz) return;
    try {
      const fresh = await getPracticeQuiz(quiz.question_id || quiz._id || quizId);
      if (fresh?.data) quiz = { ...quiz, ...fresh.data };
    } catch (err) {
      Toast.error(err.message || 'Failed to load question');
    }

    // Convert API format to form format
    let options = [];
    let correctIndex = 0;
    let matchPairs = [];

    // Handle match_pairs type
    const pairSource = Array.isArray(quiz.match_pairs) && quiz.match_pairs.length > 0
      ? quiz.match_pairs
      : quiz.options;
    if (quiz.question_type === 'match_pairs' && Array.isArray(pairSource) && pairSource.length > 0) {
      const sortedPairs = pairSource
        .filter(opt => opt.is_active !== false)
        .sort((a, b) => (a.order || 0) - (b.order || 0));

      matchPairs = sortedPairs.map((pair, idx) => ({
        id: pair._id || idx + 1,
        left_item: pair.left_item || '',
        right_item: pair.right_item || '',
        order: pair.order !== undefined ? pair.order : idx,
      }));
    } else if (Array.isArray(quiz.options) && quiz.options.length > 0) {
      // Text_text type: Filter and sort options
      const sortedOptions = quiz.options
        .filter(opt => opt.is_active !== false)
        .sort((a, b) => (a.order || 0) - (b.order || 0));

      options = sortedOptions.map((opt, idx) => ({
        id: opt._id || idx + 1,
        text: opt.option_text || opt.text || '',
        is_correct: opt.is_correct || false,
      }));

      correctIndex = options.findIndex(opt => opt.is_correct);
      if (correctIndex < 0) correctIndex = 0;
    } else if (quiz.optionsMap) {
      // Legacy format: optionsMap (A, B, C, D)
      const optionMap = quiz.optionsMap;
      const correctAnswer = quiz.correct_answer || 'A';
      let id = 1;
      ['A', 'B', 'C', 'D'].forEach((key) => {
        if (optionMap[key] && optionMap[key].trim()) {
          options.push({
            id: id++,
            text: optionMap[key],
            is_correct: key === correctAnswer,
          });
          if (key === correctAnswer) {
            correctIndex = options.length - 1;
          }
        }
      });
    } else {
      // Fallback: old individual fields
      const optionMap = {
        A: quiz.option_a_text || quiz.options?.A || '',
        B: quiz.option_b_text || quiz.options?.B || '',
        C: quiz.option_c_text || quiz.options?.C || '',
        D: quiz.option_d_text || quiz.options?.D || '',
      };
      const correctAnswer = quiz.correct_answer || 'A';
      let id = 1;
      ['A', 'B', 'C', 'D'].forEach((key) => {
        if (optionMap[key] && optionMap[key].trim()) {
          options.push({
            id: id++,
            text: optionMap[key],
            is_correct: key === correctAnswer,
          });
          if (key === correctAnswer) {
            correctIndex = options.length - 1;
          }
        }
      });
    }

    // Ensure at least 2 options for text_text, audio_text, audio_audio and image_text types
    if (
      options.length < 2 &&
      (quiz.question_type === 'text_text' ||
        quiz.question_type === 'audio_text' ||
        quiz.question_type === 'audio_audio' ||
        quiz.question_type === 'image_text' ||
        !quiz.question_type)
    ) {
      while (options.length < 2) {
        options.push({ id: options.length + 1, text: '', is_correct: false });
      }
    }

    // Ensure at least 1 pair for match_pairs type
    if (matchPairs.length === 0 && quiz.question_type === 'match_pairs') {
      matchPairs = [{ id: 1, left_item: '', right_item: '', order: 0 }];
    }

    setEditingQuiz({
      question_id: quiz.question_id || quiz.id,
      content_id: quiz.content_id || '',
      question_type: quiz.question_type || 'text_text',
      question_text: quiz.question_text || quiz.question || '',
      question_image_url: quiz.question_image_url || '',
      question_audio_url: quiz.question_audio_url || '',
      coins: quiz.coins ?? 0,
      difficulty_level: quiz.difficulty_level || 'medium',
      options: options,
      correct_answer_index: correctIndex >= 0 ? correctIndex : 0,
      match_pairs: matchPairs,
      reorder_correct_order: Array.isArray(quiz.reorder_correct_order) && quiz.reorder_correct_order.length >= 2
        ? quiz.reorder_correct_order.map((text, i) => ({ id: i + 1, text: typeof text === 'string' ? text : (text.text || '') }))
        : [{ id: 1, text: '' }, { id: 2, text: '' }],
      fill_blank_correct_order: Array.isArray(quiz.fill_blank_correct_order) && quiz.fill_blank_correct_order.length >= 1
        ? quiz.fill_blank_correct_order.map((seg, idx) => ({ id: idx + 1, title: seg.title ?? '', isBlank: Boolean(seg.isBlank) }))
        : [{ id: 1, title: '', isBlank: false }],
      explanation: quiz.explanation || '',
      points: quiz.points ?? quiz.points ?? 0,
      order: quiz.order ?? 1,
      is_active:
        typeof quiz.is_active === 'boolean' ? quiz.is_active : true,
    });
    setEditErrors({ points: '', reorder_correct_order: '', fill_blank_correct_order: '' });
    setShowEditModal(true);
  };

  const handleAddQuizClick = () => {
    setAddErrors({
      question_text: '',
      options: '',
      correct_answer: '',
      points: '',
      match_pairs: '',
      reorder_correct_order: '',
      fill_blank_correct_order: '',
    });
    setNewQuiz({
      content_id: contentId || '',
      question_type: 'text_text',
      question_text: '',
      question_image_url: '',
      question_audio_url: '',
      coins: 0,
      difficulty_level: 'medium',
      options: [
        { id: 1, text: '', is_correct: false },
        { id: 2, text: '', is_correct: false },
      ],
      correct_answer_index: 0,
      match_pairs: [
        { id: 1, left_item: '', right_item: '', order: 0 },
      ],
      reorder_correct_order: [{ id: 1, text: '' }, { id: 2, text: '' }],
      fill_blank_correct_order: [
        { id: 1, title: '', isBlank: false },
      ],
      explanation: '',
      points: 0,
      order: 1,
    });
    setShowAddModal(true);
  };

  const addMatchPair = (isEdit = false) => {
    if (isEdit) {
      const currentPairs = editingQuiz.match_pairs || [];
      const newId = currentPairs.reduce((max, item) => (typeof item.id === 'number' ? Math.max(max, item.id) : max), 0) + 1;
      setEditingQuiz({
        ...editingQuiz,
        match_pairs: [...currentPairs, { id: newId, left_item: '', right_item: '', order: currentPairs.length }],
      });
    } else {
      const currentPairs = newQuiz.match_pairs || [];
      const newId = currentPairs.reduce((max, item) => (typeof item.id === 'number' ? Math.max(max, item.id) : max), 0) + 1;
      setNewQuiz({
        ...newQuiz,
        match_pairs: [...currentPairs, { id: newId, left_item: '', right_item: '', order: currentPairs.length }],
      });
    }
  };

  const removeMatchPair = (pairId, isEdit = false) => {
    if (isEdit) {
      const currentPairs = editingQuiz.match_pairs || [];
      if (currentPairs.length <= 1) {
        Toast.error('At least 1 match pair is required');
        return;
      }
      const updatedPairs = currentPairs
        .filter(p => p.id !== pairId)
        .map((p, idx) => ({ ...p, order: idx }));
      setEditingQuiz({
        ...editingQuiz,
        match_pairs: updatedPairs,
      });
    } else {
      const currentPairs = newQuiz.match_pairs || [];
      if (currentPairs.length <= 1) {
        Toast.error('At least 1 match pair is required');
        return;
      }
      const updatedPairs = currentPairs
        .filter(p => p.id !== pairId)
        .map((p, idx) => ({ ...p, order: idx }));
      setNewQuiz({
        ...newQuiz,
        match_pairs: updatedPairs,
      });
    }
  };

  const handleQuestionImageUpload = async (file, isEdit = false) => {
    if (!file) return;
    try {
      setImageUploading(true);
      const url = await uploadAssetImage(file);
      if (isEdit) {
        setEditingQuiz((prev) => ({
          ...prev,
          question_image_url: url,
        }));
      } else {
        setNewQuiz((prev) => ({
          ...prev,
          question_image_url: url,
        }));
      }
      Toast.success('Image uploaded successfully');
    } catch (err) {
      Toast.error(err.message || 'Failed to upload image');
    } finally {
      setImageUploading(false);
    }
  };

  const updateMatchPair = (pairId, field, value, isEdit = false) => {
    if (isEdit) {
      setEditingQuiz({
        ...editingQuiz,
        match_pairs: editingQuiz.match_pairs.map(p =>
          p.id === pairId ? { ...p, [field]: value } : p
        ),
      });
    } else {
      setNewQuiz({
        ...newQuiz,
        match_pairs: newQuiz.match_pairs.map(p =>
          p.id === pairId ? { ...p, [field]: value } : p
        ),
      });
    }
  };

  // --- Reorder text helpers ---
  const addReorderItem = (isEdit = false) => {
    if (isEdit) {
      const items = editingQuiz.reorder_correct_order || [];
      const newId = items.length > 0 ? Math.max(...items.map(i => i.id)) + 1 : 1;
      setEditingQuiz({ ...editingQuiz, reorder_correct_order: [...items, { id: newId, text: '' }] });
    } else {
      const items = newQuiz.reorder_correct_order || [];
      const newId = items.length > 0 ? Math.max(...items.map(i => i.id)) + 1 : 1;
      setNewQuiz({ ...newQuiz, reorder_correct_order: [...items, { id: newId, text: '' }] });
    }
  };

  const removeReorderItem = (id, isEdit = false) => {
    if (isEdit) {
      const items = editingQuiz.reorder_correct_order || [];
      if (items.length <= 2) { Toast.error('At least 2 items are required'); return; }
      setEditingQuiz({ ...editingQuiz, reorder_correct_order: items.filter(item => item.id !== id) });
    } else {
      const items = newQuiz.reorder_correct_order || [];
      if (items.length <= 2) { Toast.error('At least 2 items are required'); return; }
      setNewQuiz({ ...newQuiz, reorder_correct_order: items.filter(item => item.id !== id) });
    }
  };

  const updateReorderItem = (id, value, isEdit = false) => {
    if (isEdit) {
      setEditingQuiz({ ...editingQuiz, reorder_correct_order: (editingQuiz.reorder_correct_order || []).map(item => item.id === id ? { ...item, text: value } : item) });
    } else {
      setNewQuiz({ ...newQuiz, reorder_correct_order: (newQuiz.reorder_correct_order || []).map(item => item.id === id ? { ...item, text: value } : item) });
    }
  };

  // --- Fill blank helpers ---
  const addFillBlankSegment = (isEdit = false) => {
    if (isEdit) {
      const segs = editingQuiz.fill_blank_correct_order || [];
      const newId = segs.reduce((max, s) => (typeof s.id === 'number' ? Math.max(max, s.id) : max), 0) + 1;
      setEditingQuiz({ ...editingQuiz, fill_blank_correct_order: [...segs, { id: newId, title: '', isBlank: false }] });
    } else {
      const segs = newQuiz.fill_blank_correct_order || [];
      const newId = segs.reduce((max, s) => (typeof s.id === 'number' ? Math.max(max, s.id) : max), 0) + 1;
      setNewQuiz({ ...newQuiz, fill_blank_correct_order: [...segs, { id: newId, title: '', isBlank: false }] });
    }
  };

  const removeFillBlankSegment = (segId, isEdit = false) => {
    if (isEdit) {
      const segs = editingQuiz.fill_blank_correct_order || [];
      if (segs.length <= 1) { Toast.error('At least 1 segment is required'); return; }
      setEditingQuiz({ ...editingQuiz, fill_blank_correct_order: segs.filter(s => s.id !== segId) });
    } else {
      const segs = newQuiz.fill_blank_correct_order || [];
      if (segs.length <= 1) { Toast.error('At least 1 segment is required'); return; }
      setNewQuiz({ ...newQuiz, fill_blank_correct_order: segs.filter(s => s.id !== segId) });
    }
  };

  const updateFillBlankSegment = (segId, field, value, isEdit = false) => {
    if (isEdit) {
      setEditingQuiz({
        ...editingQuiz,
        fill_blank_correct_order: (editingQuiz.fill_blank_correct_order || []).map(s =>
          s.id === segId ? { ...s, [field]: value } : s
        ),
      });
    } else {
      setNewQuiz({
        ...newQuiz,
        fill_blank_correct_order: (newQuiz.fill_blank_correct_order || []).map(s =>
          s.id === segId ? { ...s, [field]: value } : s
        ),
      });
    }
  };

  const addOption = (isEdit = false) => {    if (isEdit) {
      const currentOptions = editingQuiz.options || [];
      if (currentOptions.length >= 10) {
        Toast.error('Maximum 10 options allowed');
        return;
      }
      const newId = currentOptions.reduce((max, item) => (typeof item.id === 'number' ? Math.max(max, item.id) : max), 0) + 1;
      setEditingQuiz({
        ...editingQuiz,
        options: [...currentOptions, { id: newId, text: '', is_correct: false }],
      });
    } else {
      const currentOptions = newQuiz.options || [];
      if (currentOptions.length >= 10) {
        Toast.error('Maximum 10 options allowed');
        return;
      }
      const newId = currentOptions.reduce((max, item) => (typeof item.id === 'number' ? Math.max(max, item.id) : max), 0) + 1;
      setNewQuiz({
        ...newQuiz,
        options: [...currentOptions, { id: newId, text: '', is_correct: false }],
      });
    }
  };

  const removeOption = (optionId, isEdit = false) => {
    if (isEdit) {
      const currentOptions = editingQuiz.options || [];
      if (currentOptions.length <= 2) {
        Toast.error('At least 2 options are required');
        return;
      }
      const updatedOptions = currentOptions.filter(opt => opt.id !== optionId);
      let correctIndex = editingQuiz.correct_answer_index;
      const removedIndex = currentOptions.findIndex(opt => opt.id === optionId);
      if (removedIndex !== -1) {
        if (correctIndex === removedIndex) {
          correctIndex = 0; // Reset to first option if correct answer was removed
        } else if (correctIndex > removedIndex) {
          correctIndex = correctIndex - 1; // Adjust index if option before correct answer was removed
        }
      }
      setEditingQuiz({
        ...editingQuiz,
        options: updatedOptions,
        correct_answer_index: correctIndex,
      });
    } else {
      const currentOptions = newQuiz.options || [];
      if (currentOptions.length <= 2) {
        Toast.error('At least 2 options are required');
        return;
      }
      const updatedOptions = currentOptions.filter(opt => opt.id !== optionId);
      let correctIndex = newQuiz.correct_answer_index;
      const removedIndex = currentOptions.findIndex(opt => opt.id === optionId);
      if (removedIndex !== -1) {
        if (correctIndex === removedIndex) {
          correctIndex = 0; // Reset to first option if correct answer was removed
        } else if (correctIndex > removedIndex) {
          correctIndex = correctIndex - 1; // Adjust index if option before correct answer was removed
        }
      }
      setNewQuiz({
        ...newQuiz,
        options: updatedOptions,
        correct_answer_index: correctIndex,
      });
    }
  };

  const updateOptionText = (optionId, text, isEdit = false) => {
    if (isEdit) {
      setEditingQuiz({
        ...editingQuiz,
        options: editingQuiz.options.map(opt =>
          opt.id === optionId ? { ...opt, text } : opt
        ),
      });
    } else {
      setNewQuiz({
        ...newQuiz,
        options: newQuiz.options.map(opt =>
          opt.id === optionId ? { ...opt, text } : opt
        ),
      });
    }
  };

  const validateQuizPayload = (data) => {
    const errors = {
      question_text: '',
      options: '',
      correct_answer: '',
      points: '',
      match_pairs: '',
      reorder_correct_order: '',
      fill_blank_correct_order: '',
    };
    let hasError = false;

    if (!data.question_text?.trim()) {
      errors.question_text = 'Question is required';
      hasError = true;
    }

    if (
      data.question_type === 'text_text' ||
      data.question_type === 'audio_text' ||
      data.question_type === 'audio_audio' ||
      data.question_type === 'image_text'
    ) {
      const validOptions = data.options?.filter(opt => opt.text?.trim()) || [];
      if (validOptions.length < 2) {
        errors.options = 'At least 2 options are required';
        hasError = true;
      }
      if (validOptions.length > 10) {
        errors.options = 'Maximum 10 options allowed';
        hasError = true;
      }

      if (data.correct_answer_index === undefined || data.correct_answer_index === null || data.correct_answer_index < 0) {
        errors.correct_answer = 'Correct answer is required';
        hasError = true;
      }
    } else if (data.question_type === 'match_pairs') {
      const validPairs = data.match_pairs?.filter(pair => pair.left_item?.trim() && pair.right_item?.trim()) || [];
      if (validPairs.length < 2) {
        errors.match_pairs = 'Minimun 2 match pair is required';
        hasError = true;
      }
    } else if (data.question_type === 'reorder_text') {
      const validItems = (data.reorder_correct_order || []).filter(item => (item.text || '').trim());
      if (validItems.length < 2) {
        errors.reorder_correct_order = 'At least 2 items are required';
        hasError = true;
      }
    } else if (data.question_type === 'fill_blank_text') {
      const segments = data.fill_blank_correct_order || [];
      if (segments.length < 1) {
        errors.fill_blank_correct_order = 'At least 1 segment is required';
        hasError = true;
      } else if (segments.some(seg => !(seg.title || '').trim())) {
        errors.fill_blank_correct_order = 'All segments must have text';
        hasError = true;
      } else if (!segments.some(seg => seg.isBlank)) {
        errors.fill_blank_correct_order = 'At least 1 segment must be marked as a blank';
        hasError = true;
      }
    }

    // Validate points - must be a positive number
    const pointsValue = data.points !== undefined && data.points !== null && data.points !== ''
      ? Number(data.points)
      : null;

    if (pointsValue === null || isNaN(pointsValue)) {
      errors.points = 'points is required';
    } else if (pointsValue < 0) {
      errors.points = 'points must be at least 0';
    } else if (pointsValue > 999999) {
      errors.points = 'points cannot exceed 6 digits';
    }

    return { errors, hasError };
  };

  const handleAddSubmit = async () => {
    const { errors, hasError } = validateQuizPayload(newQuiz);
    setAddErrors(errors);
    if (hasError) return;

    setAdding(true);
    try {
      await createQuizQuestion({ ...newQuiz, content_id: newQuiz.content_id || contentId });
      setShowAddModal(false);
      setHasAnyQuizzes(true);
      setIsLoading(true);
      await loadQuizzes();
    } catch (err) {
      Toast.error(err.message || 'Failed to create quiz');
    } finally {
      setAdding(false);
      setIsLoading(false);
    }
  };

  const handleEditSubmit = async () => {
    const { errors, hasError } = validateQuizPayload(editingQuiz);
    setEditErrors(errors);
    if (hasError) return;

    setEditing(true);
    try {
      await updateQuizQuestion(editingQuiz);
      // Toast.success('Quiz updated successfully');
      setShowEditModal(false);
      setIsLoading(true);
      await loadQuizzes();
    } catch (err) {
      Toast.error(err.message || 'Failed to update quiz');
    } finally {
      setEditing(false);
      setIsLoading(false);
    }
  };

  const handleUploadCSV = async () => {
    if (!selectedFile) {
      Toast.error('Please select a JSON file');
      return;
    }
    setUploadingCsv(true);
    try {
      const params = {};
      if (moduleId) params.module_id = moduleId;
      if (unitId) params.unit_id = unitId;
      if (contentId) params.lesson_content_id = contentId;
      const res = await uploadPracticeJson(selectedFile, params);
      const resultData = res?.data || {};
      setUploadResult({
        successful: resultData.successful || [],
        skipped: [],
        error: resultData.errors || [],
      });
      setShowUploadModal(false);
      setSelectedFile(null);
      setShowUploadResultModal(true);
      setHasAnyQuizzes(true);
      setIsLoading(true);
      await loadQuizzes();
    } catch (err) {
      Toast.error(err.message || 'Failed to upload JSON');
    } finally {
      setUploadingCsv(false);
      setIsLoading(false);
    }
  };

  const getDifficultyBadgeClass = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case 'easy':
        return 'bg-[#9FF7C9]/30 text-[#60BC7C]/100';
      case 'medium':
        return 'bg-amber-100/60 text-amber-600';
      case 'hard':
        return 'bg-red-100/50 text-red-600';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const renderFillBlankCorrectAnswer = (segments) => {
    if (!Array.isArray(segments) || segments.length === 0) return '—';
    return segments.map((seg, idx) => (
      <span key={idx}>
        {seg.isBlank ? (
          <strong className="font-bold text-gray-900">{seg.title || '—'}</strong>
        ) : (
          <span>{seg.title}</span>
        )}
        {idx < segments.length - 1 ? ' ' : ''}
      </span>
    ));
  };

  // Cleanup: Restore scrolling when modal closes
  useEffect(() => {
    if (!showReorderModal) {
      document.body.style.overflowX = '';
    }
    return () => {
      document.body.style.overflowX = '';
    };
  }, [showReorderModal]);

  // Filter to show only active quizzes (as a backup if API doesn't filter) and sort by order
  const filteredQuizzes = useMemo(() => {
    const filtered = quizzes.filter(quiz => {
      const isActive = quiz.is_active === true || quiz.status === 'Active';
      return isActive;
    });

    // Sort by order field (ascending) to display in correct sequence
    return filtered.sort((a, b) => {
      const orderA = a.order || 0;
      const orderB = b.order || 0;
      return orderA - orderB;
    });
  }, [quizzes]);

  return (





    <div className="h-screen flex overflow-hidden">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="flex-1 bg-[#FDF6EF] flex flex-col px-4 py-4 md:p-6 overflow-hidden">
        <div className="flex-1 flex flex-col min-h-0 overflow-y-auto no-scrollbar">
          {/* Header */}
          <div className="mb-6 md:mb-1">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-2">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="lg:hidden p-2  rounded-lg hover:bg-white/50 transition-colors"
                >
                  <HiMenu className="w-6 h-6 text-gray-800" />
                </button>
                {lessonId && contentId && (
                  <button
                    onClick={() => {
                      if (moduleId && chapterId) {
                        navigate(
                          `/practice/modules/${moduleId}/units/${unitId}/lessons`,
                          {
                            state: {
                              fromName: unitName,
                              unitName,
                              moduleName,
                              chapterName,
                            },
                          }
                        );
                      }
                    }}
                    className="p-2 -ml-2 sm:ml-0 rounded-lg hover:bg-white/50 transition-colors"
                  >
                    <HiArrowLeft className="w-6 h-6 text-gray-800" />
                  </button>
                )}
                <div className="flex flex-col md:flex-row md:items-baseline">
                <h1 className="text-xl md:text-3xl font-bold text-gray-800">
                  Quiz{contentId ? ' - Content Quiz' : ''}
                </h1>
                  {fromName && (
                    <span className="text-sm md:text-lg font-semibold text-gray-600 mt-0.5 md:mt-0 md:ml-2">
                      {fromName}
                    </span>
                  )}
                </div>
              </div>

            </div>
          </div>

          {/* Filters Bar */}
          <div className="mb-10 md:mb-9 -mt-1 md:mt-3">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4">
              {/* Search Input - Full width on mobile */}
              <div className="w-full md:w-40">
                <div className="relative">
                  <HiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-450 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 md:py-1.5 text-sm border border-[#ECECEC] focus:outline-none focus:border-gray-300 bg-white"
                    style={{ borderRadius: '12px' }}
                  />
                </div>
              </div>

              {/* Action Buttons - Second line on mobile, same line on desktop */}
              <div className="flex items-center justify-between md:justify-end gap-2 w-full md:w-auto">
                <button
                  onClick={() => setShowUploadModal(true)}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-white rounded-xl shadow-md hover:shadow-lg transition-all bg-brand hover:opacity-90 whitespace-nowrap flex-shrink-0"
                >
                  <HiUpload className="w-4 h-4" />
                  <span>Upload File</span>
                </button>
                <button
                  onClick={() => {
                    if (!filteredQuizzes.length) return;
                    setReorderQuizzes(filteredQuizzes);
                    setShowReorderModal(true);
                  }}
                  disabled={!filteredQuizzes.length}
                  className={`flex items-center w-26 gap-2 px-3 py-2 text-sm font-semibold rounded-xl bg-brand text-white hover:shadow-lg shadow-md whitespace-nowrap flex-shrink-0 transition-all 
                    
                  }`}
                >
                  Edit Order
                </button>
                <button
                  onClick={handleAddQuizClick}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm font-semibold text-white rounded-xl shadow-md hover:shadow-lg transition-all bg-brand hover:opacity-90 whitespace-nowrap"
                >
                  <span className="text-base">+</span>
                  <span>Add Quiz</span>
                </button>
              </div>
            </div>
          </div>
          {/* Quiz List */}
          <div>
            {isLoading || paginationLoading ? (
              <div className="fixed inset-0 ml-0 lg:ml-64 z-30 flex items-center justify-center pointer-events-none">
                <div className="flex items-center gap-3 pointer-events-auto" role="status" aria-live="polite">
                  <svg
                    className="w-12 h-12 animate-spin"
                    viewBox="0 0 100 100"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <defs>
                      <linearGradient id="purple-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#E9D5FF" />
                        <stop offset="25%" stopColor="#C084FC" />
                        <stop offset="50%" stopColor="#A855F7" />
                        <stop offset="75%" stopColor="#9333EA" />
                        <stop offset="100%" stopColor="#6B21A8" />
                      </linearGradient>
                    </defs>
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      stroke="url(#purple-gradient)"
                      strokeWidth="6"
                      strokeLinecap="round"
                      strokeDasharray="60 200"
                      fill="none"
                      transform="rotate(-90 50 50)"
                    />
                  </svg>
                </div>

              </div>
            ) : filteredQuizzes.length === 0 ? (
              <div className="w-full min-h-[600px] flex items-center justify-center">
                <div className="max-w-md mx-auto text-center">
                  {hasAnyQuizzes && String(debouncedSearch || '').trim().length > 0 ? (
                    <>
                      <p className="text-sm text-gray-500 mb-6">No quizzes found. Try a different keyword.</p>
                    </>
                  ) : (
                    <>
                      <div className="mb-6">
                        <svg
                          className="w-24 h-24 mx-auto text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                      </div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-2">
                        No quizzes added yet
                      </h3>
                      <p className="text-sm text-gray-500 mb-6">
                        {error || 'Start by adding the first quiz'}
                      </p>
                      <button
                        onClick={handleAddQuizClick}
                        className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white rounded-xl shadow-md hover:shadow-lg transition-all bg-brand hover:opacity-90"
                      >
                        <span className="text-base">+</span>
                        <span>Add Quiz</span>
                      </button>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className="quiz-grid-responsive grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-3  3xl:grid-cols-4 -mt-4 gap-4">
                {filteredQuizzes.map((quiz) => {
                  return (
                    <div
                      key={quiz.id}
                      className="bg-white rounded-xl p-4 sm:p-4 transition-shadow relative overflow-hidden hover:shadow-lg flex flex-col h-full"
                      style={{ boxShadow: '0px 3px 8px 0px #93939340' }}
                    >
                      {/* Row 1: Question + Status Dropdown */}
                      <div className="flex items-start justify-between mb-4 gap-3">
                        <h3 className="text-sm sm:text-base font-bold text-gray-900 flex-1 line-clamp-2">
                          {quiz.question}
                        </h3>

                      </div>

                      {/* Row 2: Options or Match Pairs */}
                      <div className={quiz.question_type === 'match_pairs' || quiz.question_type === 'reorder_text' || quiz.question_type === 'fill_blank_text' ? 'mb-20' : 'mb-1.5'}>
                        {quiz.question_type === 'match_pairs' ? (
                          // Match Pairs Display
                          <div className="space-y-2">
                            {Array.isArray(quiz.match_pairs) && quiz.match_pairs.length > 0 ? (
                              quiz.match_pairs.map((pair, idx) => (
                                <div key={pair._id || idx} className="flex items-center gap-2 text-xs sm:text-sm">
                                  <span className="font-semibold text-gray-700 min-w-[60px]">
                                    {pair.left_item || ''}
                                  </span>
                                  <span className="text-gray-400">→</span>
                                  <span className="text-gray-800">
                                    {pair.right_item || ''}
                                  </span>
                                </div>
                              ))
                            ) : (
                              <div className="text-xs text-gray-500">No pairs available</div>
                            )}
                          </div>
                        ) : quiz.question_type === 'reorder_text' ? (
                          // Reorder Text Display
                          <div className="space-y-1">
                            {Array.isArray(quiz.reorder_correct_order) && quiz.reorder_correct_order.length > 0 ? (
                              quiz.reorder_correct_order.map((item, idx) => (
                                <div key={idx} className="flex items-center gap-2 text-xs sm:text-sm">
                                  <span className="font-semibold text-gray-500 w-4 text-right flex-shrink-0">{idx + 1}.</span>
                                  <span className="text-gray-800">{typeof item === 'string' ? item : item.text}</span>
                                </div>
                              ))
                            ) : (
                              <div className="text-xs text-gray-500">No items</div>
                            )}
                          </div>
                        ) : quiz.question_type === 'fill_blank_text' ? (
                          // Fill in the Blank Display
                          <div className="flex flex-wrap gap-1 text-xs sm:text-sm break-words">
                            {Array.isArray(quiz.fill_blank_correct_order) && quiz.fill_blank_correct_order.length > 0 ? (
                              quiz.fill_blank_correct_order.map((seg, idx) => (
                                seg.isBlank ? (
                                  <span key={idx} className="font-semibold text-brand">[____]</span>
                                ) : (
                                  <span key={idx} className="text-gray-600">{seg.title}</span>
                                )
                              ))
                            ) : (
                              <div className="text-xs text-gray-500">No segments</div>
                            )}
                          </div>
                        ) : (
                          // Options Display for text_text type
                          <div className="mb-1.5">
                            <div className="grid grid-cols-2 gap-x-1 gap-y-1 sm:gap-x-1 sm:gap-y-1">
                              {Array.isArray(quiz.options) ? (
                                // New format: options array from API
                                quiz.options
                                  .filter(opt => opt.is_active !== false)
                                  .sort((a, b) => (a.order || 0) - (b.order || 0))
                                  .map((option, idx) => {
                                    const label = String.fromCharCode(65 + idx); // A, B, C, D, etc.
                                    const optionText = option.option_text || option.text || '';
                                    return (
                                      <div key={option._id || idx} className="px-1">
                                        <span className="text-xs font-semibold text-gray-700">
                                          {label}.
                                        </span>
                                        <span className="ml-1 text-xs sm:text-sm text-gray-800">
                                          {optionText}
                                        </span>
                                      </div>
                                    );
                                  })
                              ) : (
                                // Legacy format: optionsMap object
                                Object.entries(quiz.optionsMap || quiz.options || {}).map(([key, value]) => (
                                  <div key={key} className="px-1">
                                    <span className="text-xs font-semibold text-gray-700">
                                      {key}.
                                    </span>
                                    <span className="ml-1 text-xs sm:text-sm text-gray-800">
                                      {value}
                                    </span>
                                  </div>
                                ))
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Lesson Name */}
                      {quiz.lesson_name && (
                        <div className="mb-2 px-1">
                          <span className="text-gray-500 text-xs font-medium mr-1">
                            Lesson:
                          </span>
                          <span className="text-sm font-medium text-gray-700">
                            {quiz.lesson_name}
                          </span>
                        </div>
                      )}

                      {/* Row 4: Correct Answer Box - MCQ + fill blank */}
                      {quiz.question_type !== 'match_pairs' && quiz.question_type !== 'reorder_text' && (
                        <div className="mb-20 p-2 mt-auto bg-gray-100/50 rounded-lg border border-gray-200/50 min-w-0">
                          <p className="text-xs font-medium text-gray-600 mb-0.5">
                            Correct Answer :
                          </p>
                          {quiz.question_type === 'fill_blank_text' ? (
                            <p className="text-sm text-gray-800 break-words">
                              {renderFillBlankCorrectAnswer(quiz.fill_blank_correct_order)}
                            </p>
                          ) : (
                            <p className="text-sm font-medium text-gray-700 break-all line-clamp-3" title={quiz.correct_answer_text}>
                              {quiz.correct_answer_text}
                            </p>
                          )}
                        </div>
                      )}

                      {/* Row 5: Actions */}
                      <div className="absolute bottom-3   left-4 right-3 flex flex-col gap-1.5">
                        {/* First line: points + edit/delete */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1.5 text-xs font-medium ml-0 px-2 py-1.5 rounded-lg bg-gray-100 text-gray-700" title="Points">
                            <span>Points:</span>
                            <span className="text-gray-700">{quiz.points || 0}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(quiz.id);
                              }}
                              className="w-8 h-8 text-sm font-bold rounded-lg transition-colors hover:opacity-90 text-brand bg-[#EFEBF3] flex items-center justify-center"
                              title="Edit Quiz"
                            >
                              <HiPencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(quiz.id);
                              }}
                              className="w-8 h-8 font-bold text-sm rounded-lg transition-colors hover:opacity-90 text-[#F76464] bg-[#FFF0F0] flex items-center justify-center"
                              title="Delete Quiz"
                            >
                              <HiTrash className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        {/* Second line: difficulty (left) + question type (right) */}
                        <div className="flex items-center justify-between mt-0.5">
                          <span
                            className={`inline-flex items-center justify-center min-w-[64px] px-3 py-2 text-[11px] font-semibold rounded-lg ${getDifficultyBadgeClass(quiz.difficulty_level)}`}
                          >
                            {(quiz.difficulty_level || 'medium')
                              .charAt(0)
                              .toUpperCase() +
                              (quiz.difficulty_level || 'medium').slice(1)}
                          </span>
                          <span className="inline-flex items-center whitespace-nowrap justify-center min-w-[78px] px-2 py-[7px] text-[11px] font-semibold rounded-lg bg-blue-50 text-blue-700 border border-blue-100">
                            {quiz.question_type === 'match_pairs'
                              ? 'Match Pairs'
                              : quiz.question_type === 'audio_text'
                                ? 'Audio Question (Text Options)'
                                : quiz.question_type === 'audio_audio'
                                  ? 'Audio Question (Audio Options)'
                                  : quiz.question_type === 'image_text'
                                    ? 'Image Question'
                                    : quiz.question_type === 'reorder_text'
                                      ? 'Reorder Text'
                                      : quiz.question_type === 'fill_blank_text'
                                        ? 'Fill in the Blank'
                                        : quiz.question_type === 'text_text'
                                          ? 'Text Question'
                                          : 'Text Question'}
                          </span>
                        </div>
                      </div>
                      {/* Decorative graphic */}
                      <div className="absolute -mr-4 bottom-0 right-0 opacity-15 pointer-events-none">
                        <img
                          src={require('../../../assets/round.png')}
                          alt="Round decoration"
                          className="w-28 h-28 sm:w-[150px] sm:h-[120px] object-contain"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Pagination */}
            {((totalPages > 1) || (totalPages === 1 && totalCount === 16)) && !isLoading && !paginationLoading && (
              <div className="mt-6 flex justify-end border-t border-gray-200 pt-4">
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                    disabled={page === 1}
                    className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    aria-label="Previous page"
                  >
                    <HiChevronLeft className="w-4 h-4" />
                  </button>
                  <div className="flex items-center gap-1">
                    {getPaginationRange().map((pageNum, idx) =>
                      pageNum === 'dots' ? (
                        <span key={`dots-${idx}`} className="px-2 text-sm text-gray-500">
                          ...
                        </span>
                      ) : (
                        <button
                          key={pageNum}
                          onClick={() => setPage(pageNum)}
                          className={`min-w-[30px] h-8 px-2 rounded-lg text-sm font-medium transition-colors ${pageNum === page
                            ? 'bg-brand border border-brand text-white shadow-sm hover:opacity-90'
                            : 'text-gray-900 hover:bg-gray-100'
                            }`}
                          aria-current={pageNum === page ? 'page' : undefined}
                        >
                          {pageNum}
                        </button>
                      )
                    )}
                  </div>
                  <button
                    onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                    disabled={page === totalPages}
                    className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    aria-label="Next page"
                  >
                    <HiChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Quiz Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-hidden">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full flex flex-col max-h-full">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
              <h2 className="text-lg font-bold text-gray-800">Add New Quiz</h2>
              {/* <button
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  setShowAddModal(false);
                  setActiveDropdown(null);
                }}
              >
                ✕
              </button> */}
            </div>
            <div className="px-6 py-6 space-y-3 overflow-y-auto flex-1 no-scrollbar">

              {/* Question Type */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Question Type <span className="text-red-500">*</span>
                </label>
                <div className="relative custom-dropdown-container">
                  <button
                    type="button"
                    onClick={() => setActiveDropdown(activeDropdown === 'addQuestionType' ? null : 'addQuestionType')}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none flex items-center justify-between text-left bg-white"
                  >
                    <span className="truncate capitalize">
                      {newQuiz.question_type === 'match_pairs'
                        ? 'Match Pairs'
                        : newQuiz.question_type === 'audio_text'
                          ? 'Audio Question (Text Options)'
                          : newQuiz.question_type === 'audio_audio'
                            ? 'Audio Question (Audio Options)'
                            : newQuiz.question_type === 'image_text'
                              ? 'Image Question'
                              : newQuiz.question_type === 'reorder_text'
                                ? 'Reorder Text'
                                : newQuiz.question_type === 'fill_blank_text'
                                  ? 'Fill in the Blank'
                                  : 'Text Question'}
                    </span>
                    <HiChevronDown className={`w-4 h-4 transition-transform duration-300 ease-in-out ${activeDropdown === 'addQuestionType' ? 'rotate-180' : ''}`} />
                  </button>

                  {activeDropdown === 'addQuestionType' && (
                    <div className="absolute z-[60] mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden py-1">
                      <button
                        type="button"
                        onClick={() => {
                          setNewQuiz({ ...newQuiz, question_type: 'text_text' });
                          setActiveDropdown(null);
                        }}
                        className={`w-full px-4 py-2 text-sm text-left hover:bg-gray-100 transition-colors ${newQuiz.question_type === 'text_text' ? 'bg-gray-100 text-black font-semibold' : 'text-gray-700'
                          }`}
                      >
                        Text Question
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setNewQuiz({ ...newQuiz, question_type: 'match_pairs' });
                          setActiveDropdown(null);
                        }}
                        className={`w-full px-4 py-2 text-sm text-left hover:bg-gray-100 transition-colors ${newQuiz.question_type === 'match_pairs' ? 'bg-gray-100 text-black font-semibold' : 'text-gray-700'
                          }`}
                      >
                        Match Pairs
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setNewQuiz({ ...newQuiz, question_type: 'audio_text' });
                          setActiveDropdown(null);
                        }}
                        className={`w-full px-4 py-2 text-sm text-left hover:bg-gray-100 transition-colors ${newQuiz.question_type === 'audio_text' ? 'bg-gray-100 text-black font-semibold' : 'text-gray-700'
                          }`}
                      >
                        Audio Question (Text Options)
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setNewQuiz({ ...newQuiz, question_type: 'audio_audio' });
                          setActiveDropdown(null);
                        }}
                        className={`w-full px-4 py-2 text-sm text-left hover:bg-gray-100 transition-colors ${newQuiz.question_type === 'audio_audio' ? 'bg-gray-100 text-black font-semibold' : 'text-gray-700'
                          }`}
                      >
                        Audio Question (Audio Options)
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setNewQuiz({ ...newQuiz, question_type: 'image_text' });
                          setActiveDropdown(null);
                        }}
                        className={`w-full px-4 py-2 text-sm text-left hover:bg-gray-100 transition-colors ${newQuiz.question_type === 'image_text' ? 'bg-gray-100 text-black font-semibold' : 'text-gray-700'
                          }`}
                      >
                        Image Question
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setNewQuiz({ ...newQuiz, question_type: 'reorder_text' });
                          setActiveDropdown(null);
                        }}
                        className={`w-full px-4 py-2 text-sm text-left hover:bg-gray-100 transition-colors ${newQuiz.question_type === 'reorder_text' ? 'bg-gray-100 text-black font-semibold' : 'text-gray-700'
                          }`}
                      >
                        Reorder Text
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setNewQuiz({ ...newQuiz, question_type: 'fill_blank_text' });
                          setActiveDropdown(null);
                        }}
                        className={`w-full px-4 py-2 text-sm text-left hover:bg-gray-100 transition-colors ${newQuiz.question_type === 'fill_blank_text' ? 'bg-gray-100 text-black font-semibold' : 'text-gray-700'
                          }`}
                      >
                        Fill in the Blank
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Question */}
              <div>
                <label className="block text-xs -mt-1 font-medium text-gray-700 mb-1">
                  Question <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={newQuiz.question_text}
                  onChange={(e) =>
                    setNewQuiz({ ...newQuiz, question_text: e.target.value })
                  }
                  rows={1}
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none resize-none ${addErrors.question_text ? 'border-red-500' : 'border-gray-300'
                    }`}
                  placeholder="Enter quiz question"
                />
                {addErrors.question_text && (
                  <p className="mt-1 text-xs text-red-500">
                    {addErrors.question_text}
                  </p>
                )}
                {newQuiz.question_type === 'audio_text' && (
                  <div className="mt-3">
                    <label className="block text-xs font-medium text-gray-700 mb-1">Question audio URL</label>
                    <input
                      type="url"
                      value={newQuiz.question_audio_url || ''}
                      onChange={(e) => setNewQuiz({ ...newQuiz, question_audio_url: e.target.value })}
                      placeholder="https://cdn.example.com/prompt.mp3"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none"
                    />
                  </div>
                )}
                {newQuiz.question_type === 'image_text' && (
                  <div className="mt-3 space-y-2">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Upload Question Image
                      </label>
                      <div className="flex items-center gap-2">
                        <label className="inline-flex items-center px-3 py-2 text-xs font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-200">
                          <HiUpload className="w-4 h-4 mr-1" />
                          <span>{imageUploading ? 'Uploading...' : 'Choose Image'}</span>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            disabled={imageUploading}
                            onChange={(e) => {
                              const file = e.target.files && e.target.files[0];
                              if (file) {
                                handleQuestionImageUpload(file, false);
                              }
                            }}
                          />
                        </label>
                        {newQuiz.question_image_url && (
                          <span className="text-[11px] text-green-600 truncate max-w-[140px]">
                            Image selected
                          </span>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Image URL (question_image_url)
                      </label>
                      <input
                        type="text"
                        value={newQuiz.question_image_url || ''}
                        onChange={(e) =>
                          setNewQuiz({ ...newQuiz, question_image_url: e.target.value })
                        }
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none"
                        placeholder="Paste image URL or use uploader above"
                      />
                    </div>
                    {newQuiz.question_image_url && (
                      <div className="mt-1">
                        <img
                          src={newQuiz.question_image_url}
                          alt="Question"
                          className="max-h-32 rounded-md border border-gray-200 object-contain"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Difficulty Level */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Difficulty Level
                </label>
                <div className="relative custom-dropdown-container">
                  <button
                    type="button"
                    onClick={() => setActiveDropdown(activeDropdown === 'addDifficulty' ? null : 'addDifficulty')}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none flex items-center justify-between text-left bg-white"
                  >
                    <span className="truncate capitalize">
                      {newQuiz.difficulty_level || 'medium'}
                    </span>
                    <HiChevronDown className={`w-4 h-4 transition-transform duration-300 ease-in-out ${activeDropdown === 'addDifficulty' ? 'rotate-180' : ''}`} />
                  </button>
                  {activeDropdown === 'addDifficulty' && (
                    <div className="absolute z-[60] mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden py-1">
                      {['easy', 'medium', 'hard'].map((level) => (
                        <button
                          key={level}
                          type="button"
                          onClick={() => {
                            setNewQuiz({ ...newQuiz, difficulty_level: level });
                            setActiveDropdown(null);
                          }}
                          className={`w-full px-4 py-2 text-sm text-left hover:bg-gray-100 transition-colors capitalize ${(newQuiz.difficulty_level || 'medium') === level ? 'bg-gray-100 text-black font-semibold' : 'text-gray-700'}`}
                        >
                          {level}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Options - Only show for text_text, audio_text, audio_audio and image_text types */}
              {(newQuiz.question_type === 'text_text' ||
                newQuiz.question_type === 'audio_text' ||
                newQuiz.question_type === 'audio_audio' ||
                newQuiz.question_type === 'image_text') && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-xs font-medium text-gray-700">
                        Options <span className="text-red-500">*</span> <span className="text-gray-500 font-normal">(2-10 options)</span>
                      </label>
                      {newQuiz.options.length < 10 && (
                        <button
                          type="button"
                          onClick={() => addOption(false)}
                          className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-brand hover:bg-brand/10 rounded-lg transition-colors"
                        >
                          <HiPlus className="w-4 h-4" />
                          Add Option
                        </button>
                      )}
                    </div>
                    <div className="space-y-2">
                      {newQuiz.options.map((option, index) => (
                        <div key={option.id} className="flex items-start gap-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs font-medium text-gray-600">
                                Option {index + 1}
                                {index < 2 && <span className="text-red-500 ml-1">*</span>}
                              </span>
                            </div>
                            <input
                              type="text"
                              value={option.text}
                              onChange={(e) => updateOptionText(option.id, e.target.value, false)}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none"
                              placeholder={`Enter option ${index + 1}`}
                            />
                          </div>
                          {newQuiz.options.length > 2 && (
                            <button
                              type="button"
                              onClick={() => removeOption(option.id, false)}
                              className="mt-6 p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              title="Remove option"
                            >
                              <HiX className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    {addErrors.options && (
                      <p className="mt-1 text-xs text-red-500">
                        {addErrors.options}
                      </p>
                    )}
                  </div>
                )}

              {/* Match Pairs - Only show for match_pairs type */}
              {newQuiz.question_type === 'match_pairs' && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-xs font-medium text-gray-700">
                      Match Pairs <span className="text-red-500">*</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => addMatchPair(false)}
                      className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-brand hover:bg-brand/10 rounded-lg transition-colors"
                    >
                      <HiPlus className="w-4 h-4" />
                      Add Pair
                    </button>
                  </div>
                  <div className="space-y-3">
                    {newQuiz.match_pairs.map((pair, index) => (
                      <div key={pair.id} className="border border-gray-200 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-gray-600">
                            Pair {index + 1} <span className="text-red-500">*</span>
                          </span>
                          {newQuiz.match_pairs.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeMatchPair(pair.id, false)}
                              className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors"
                              title="Remove pair"
                            >
                              <HiX className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Left Item</label>
                            <input
                              type="text"
                              value={pair.left_item}
                              onChange={(e) => updateMatchPair(pair.id, 'left_item', e.target.value, false)}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none"
                              placeholder="e.g., France"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Right Item</label>
                            <input
                              type="text"
                              value={pair.right_item}
                              onChange={(e) => updateMatchPair(pair.id, 'right_item', e.target.value, false)}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none"
                              placeholder="e.g., Paris"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {addErrors.match_pairs && (
                    <p className="mt-1 text-xs text-red-500">
                      {addErrors.match_pairs}
                    </p>
                  )}
                </div>
              )}

              {/* Reorder Text - Only show for reorder_text type */}
              {newQuiz.question_type === 'reorder_text' && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-xs font-medium text-gray-700">
                      Items (correct order) <span className="text-red-500">*</span> <span className="text-gray-500 font-normal">(min 2, drag to reorder)</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => addReorderItem(false)}
                      className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-brand hover:bg-brand/10 rounded-lg transition-colors"
                    >
                      <HiPlus className="w-4 h-4" />
                      Add Item
                    </button>
                  </div>
                  <DndContext
                    modifiers={[restrictToVerticalAxis]}
                    onDragEnd={({ active, over }) => {
                      if (over && active.id !== over.id) {
                        const items = newQuiz.reorder_correct_order || [];
                        const oldIdx = items.findIndex(item => item.id === active.id);
                        const newIdx = items.findIndex(item => item.id === over.id);
                        if (oldIdx !== -1 && newIdx !== -1) {
                          setNewQuiz({ ...newQuiz, reorder_correct_order: arrayMove(items, oldIdx, newIdx) });
                        }
                      }
                    }}
                  >
                    <SortableContext items={(newQuiz.reorder_correct_order || []).map(item => item.id)}>
                      <div className="space-y-2">
                        {(newQuiz.reorder_correct_order || []).map((item, index) => (
                          <SortableReorderRow
                            key={item.id}
                            id={item.id}
                            index={index}
                            value={item.text}
                            onChange={(val) => updateReorderItem(item.id, val, false)}
                            onRemove={(newQuiz.reorder_correct_order || []).length > 2 ? () => removeReorderItem(item.id, false) : null}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                  {addErrors.reorder_correct_order && (
                    <p className="mt-1 text-xs text-red-500">{addErrors.reorder_correct_order}</p>
                  )}
                </div>
              )}

              {/* Fill in the Blank - Only show for fill_blank_text type */}
              {newQuiz.question_type === 'fill_blank_text' && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-xs font-medium text-gray-700">
                      Segments <span className="text-red-500">*</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => addFillBlankSegment(false)}
                      className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-brand hover:bg-brand/10 rounded-lg transition-colors"
                    >
                      <HiPlus className="w-4 h-4" />
                      Add Segment
                    </button>
                  </div>
                  <p className="text-[11px] text-gray-400 mb-2">Static = shown as text · Blank = user must fill in</p>
                  <div className="space-y-2">
                    {(newQuiz.fill_blank_correct_order || []).map((seg, index) => (
                      <div key={seg.id} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={seg.title}
                          onChange={(e) => updateFillBlankSegment(seg.id, 'title', e.target.value, false)}
                          className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none"
                          placeholder={seg.isBlank ? 'Blank answer text' : 'Static text'}
                        />
                        <label className="flex items-center gap-1 text-xs text-gray-600 flex-shrink-0 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={seg.isBlank}
                            onChange={(e) => updateFillBlankSegment(seg.id, 'isBlank', e.target.checked, false)}
                            className="rounded"
                          />
                          Blank
                        </label>
                        {(newQuiz.fill_blank_correct_order || []).length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeFillBlankSegment(seg.id, false)}
                            className="p-2 text-brand hover:bg-brand/10 rounded-lg transition-colors"
                          >
                            <HiX className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  {addErrors.fill_blank_correct_order && (
                    <p className="mt-1 text-xs text-red-500">{addErrors.fill_blank_correct_order}</p>
                  )}
                </div>
              )}

              {/* Correct answer & points */}
              <div
                className={`grid grid-cols-1 ${newQuiz.question_type === 'text_text' ||
                    newQuiz.question_type === 'audio_text' ||
                    newQuiz.question_type === 'audio_audio' ||
                    newQuiz.question_type === 'image_text'
                    ? 'md:grid-cols-2'
                    : ''
                  } gap-4`}
              >
                {(newQuiz.question_type === 'text_text' ||
                  newQuiz.question_type === 'audio_text' ||
                  newQuiz.question_type === 'audio_audio' ||
                  newQuiz.question_type === 'image_text') && (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Correct Answer <span className="text-red-500">*</span>
                      </label>
                      <div className="relative custom-dropdown-container">
                        <button
                          type="button"
                          onClick={() => setActiveDropdown(activeDropdown === 'addCorrect' ? null : 'addCorrect')}
                          className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none flex items-center justify-between text-left bg-white ${addErrors.correct_answer ? 'border-red-500' : 'border-gray-300'
                            }`}
                        >
                          <span className="truncate">
                            {newQuiz.correct_answer_index !== undefined && newQuiz.correct_answer_index >= 0 && newQuiz.options[newQuiz.correct_answer_index]
                              ? `Option ${newQuiz.correct_answer_index + 1}: ${newQuiz.options[newQuiz.correct_answer_index].text || '(Empty)'}`
                              : 'Select Answer'}
                          </span>
                          <HiChevronDown className={`w-4 h-4  transition-transform duration-300 ease-in-out ${activeDropdown === 'addCorrect' ? 'rotate-180' : ''}`} />
                        </button>

                        {activeDropdown === 'addCorrect' && (
                          <div className="absolute z-[60] mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden py-1 max-h-60 overflow-y-auto">
                            {newQuiz.options.map((option, index) => (
                              <button
                                key={option.id}
                                type="button"
                                onClick={() => {
                                  setNewQuiz({ ...newQuiz, correct_answer_index: index });
                                  setActiveDropdown(null);
                                }}
                                className={`w-full px-4 py-2 text-sm text-left hover:bg-gray-100 transition-colors ${newQuiz.correct_answer_index === index ? 'bg-gray-100 text-black font-semibold' : 'text-gray-700'
                                  }`}
                              >
                                Option {index + 1}: {option.text || '(Empty)'}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      {addErrors.correct_answer && (
                        <p className="mt-1 text-xs text-red-500">
                          {addErrors.correct_answer}
                        </p>
                      )}
                    </div>
                  )}

                <div>
                  <label className="block text-xs font-medium text-gray-700  mb-1 md:mb-1 ">
                    points <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    inputMode="numeric"
                    maxLength={6}
                    value={newQuiz.points}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Prevent entering more than 6 digits (999999)
                      if (value && !isNaN(parseInt(value)) && parseInt(value) > 999999) {
                        setAddErrors({ ...addErrors, points: 'points cannot exceed 6 digits ' });
                        return;
                      }
                      // Prevent entering more than 6 digits by checking string length
                      if (value && value.length > 6) {
                        setAddErrors({ ...addErrors, points: 'points cannot exceed 6 digits' });
                        return;
                      }
                      setNewQuiz({ ...newQuiz, points: value });
                      if (addErrors.points) setAddErrors({ ...addErrors, points: '' });
                    }}
                    className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none ${addErrors.points ? 'border-red-500' : 'border-gray-300'
                      }`}
                    placeholder="Enter points"
                  />

                  {addErrors.points && (
                    <p className="mt-1 text-xs text-red-500">
                      {addErrors.points}
                    </p>
                  )}
                </div>
              </div>




              {/* Order */}
              {/* <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Order
                </label>
                <input
                  type="number"
                  value={newQuiz.order}
                  onChange={(e) =>
                    setNewQuiz({
                      ...newQuiz,
                      order: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none"
                  placeholder="Display order"
                />
              </div> */}

              {/* Explanation */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Explanation
                </label>
                <textarea
                  value={newQuiz.explanation}
                  onChange={(e) =>
                    setNewQuiz({ ...newQuiz, explanation: e.target.value })
                  }
                  rows={1}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none resize-none"
                  placeholder="Optional explanation for the answer"
                />
              </div>
            </div>
            <div className="px-6 py-4 flex justify-end gap-3 border-t border-gray-200 flex-shrink-0">
              <button
                onClick={() => setShowAddModal(false)}
                disabled={adding}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleAddSubmit}
                disabled={adding}
                className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {adding ? (
                  <>
                    <svg
                      className="animate-spin h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Adding...
                  </>
                ) : (
                  'Add Quiz'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Quiz Modal */}
      {
        showEditModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full flex flex-col max-h-full">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                <h2 className="text-lg font-bold text-gray-800">Edit Quiz</h2>
                {/* <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    setShowEditModal(false);
                    setActiveDropdown(null);
                  }}
                >
                  ✕
                </button> */}
              </div>
              <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1 no-scrollbar">

                {/* Question Type */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Question Type <span className="text-red-500">*</span>
                  </label>
                  <div className="relative custom-dropdown-container">
                    <button
                      type="button"
                      onClick={() => setActiveDropdown(activeDropdown === 'editQuestionType' ? null : 'editQuestionType')}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none flex items-center justify-between text-left bg-white"
                    >
                      <span className="truncate capitalize">
                        {editingQuiz.question_type === 'match_pairs'
                          ? 'Match Pairs'
                          : editingQuiz.question_type === 'audio_text'
                            ? 'Audio Question (Text Options)'
                            : editingQuiz.question_type === 'audio_audio'
                              ? 'Audio Question (Audio Options)'
                              : editingQuiz.question_type === 'image_text'
                                ? 'Image Question'
                                : editingQuiz.question_type === 'reorder_text'
                                  ? 'Reorder Text'
                                  : editingQuiz.question_type === 'fill_blank_text'
                                    ? 'Fill in the Blank'
                                    : 'Text Question'}
                      </span>
                      <HiChevronDown className={`w-4 h-4 transition-transform duration-300 ease-in-out ${activeDropdown === 'editQuestionType' ? 'rotate-180' : ''}`} />
                    </button>

                    {activeDropdown === 'editQuestionType' && (
                      <div className="absolute z-[60] mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden py-1">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingQuiz({ ...editingQuiz, question_type: 'text_text' });
                            setActiveDropdown(null);
                          }}
                          className={`w-full px-4 py-2 text-sm text-left hover:bg-gray-100 transition-colors ${editingQuiz.question_type === 'text_text' ? 'bg-gray-100 text-black font-semibold' : 'text-gray-700'
                            }`}
                        >
                          Text Question
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setEditingQuiz({ ...editingQuiz, question_type: 'match_pairs' });
                            setActiveDropdown(null);
                          }}
                          className={`w-full px-4 py-2 text-sm text-left hover:bg-gray-100 transition-colors ${editingQuiz.question_type === 'match_pairs' ? 'bg-gray-100 text-black font-semibold' : 'text-gray-700'
                            }`}
                        >
                          Match Pairs
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setEditingQuiz({ ...editingQuiz, question_type: 'audio_text' });
                            setActiveDropdown(null);
                          }}
                          className={`w-full px-4 py-2 text-sm text-left hover:bg-gray-100 transition-colors ${editingQuiz.question_type === 'audio_text'
                              ? 'bg-gray-100 text-black font-semibold'
                              : 'text-gray-700'
                            }`}
                        >
                          Audio Question (Text Options)
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setEditingQuiz({ ...editingQuiz, question_type: 'audio_audio' });
                            setActiveDropdown(null);
                          }}
                          className={`w-full px-4 py-2 text-sm text-left hover:bg-gray-100 transition-colors ${editingQuiz.question_type === 'audio_audio'
                              ? 'bg-gray-100 text-black font-semibold'
                              : 'text-gray-700'
                            }`}
                        >
                          Audio Question (Audio Options)
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setEditingQuiz({ ...editingQuiz, question_type: 'image_text' });
                            setActiveDropdown(null);
                          }}
                          className={`w-full px-4 py-2 text-sm text-left hover:bg-gray-100 transition-colors ${editingQuiz.question_type === 'image_text'
                              ? 'bg-gray-100 text-black font-semibold'
                              : 'text-gray-700'
                            }`}
                        >
                          Image Question
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setEditingQuiz({ ...editingQuiz, question_type: 'reorder_text' });
                            setActiveDropdown(null);
                          }}
                          className={`w-full px-4 py-2 text-sm text-left hover:bg-gray-100 transition-colors ${editingQuiz.question_type === 'reorder_text'
                              ? 'bg-gray-100 text-black font-semibold'
                              : 'text-gray-700'
                            }`}
                        >
                          Reorder Text
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setEditingQuiz({ ...editingQuiz, question_type: 'fill_blank_text' });
                            setActiveDropdown(null);
                          }}
                          className={`w-full px-4 py-2 text-sm text-left hover:bg-gray-100 transition-colors ${editingQuiz.question_type === 'fill_blank_text'
                              ? 'bg-gray-100 text-black font-semibold'
                              : 'text-gray-700'
                            }`}
                        >
                          Fill in the Blank
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Question */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Question <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={editingQuiz.question_text}
                    onChange={(e) =>
                      setEditingQuiz({
                        ...editingQuiz,
                        question_text: e.target.value,
                      })
                    }
                    rows={1}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none resize-none"
                    placeholder="Enter quiz question"
                  />
                  {editingQuiz.question_type === 'audio_text' && (
                  <div className="mt-3">
                    <label className="block text-xs font-medium text-gray-700 mb-1">Question audio URL</label>
                    <input
                      type="url"
                      value={editingQuiz.question_audio_url || ''}
                      onChange={(e) => setEditingQuiz({ ...editingQuiz, question_audio_url: e.target.value })}
                      placeholder="https://cdn.example.com/prompt.mp3"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none"
                    />
                  </div>
                )}
                {editingQuiz.question_type === 'image_text' && (
                    <div className="mt-3 space-y-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Upload Question Image
                        </label>
                        <div className="flex items-center gap-2">
                          <label className="inline-flex items-center px-3 py-2 text-xs font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-200">
                            <HiUpload className="w-4 h-4 mr-1" />
                            <span>{imageUploading ? 'Uploading...' : 'Choose Image'}</span>
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              disabled={imageUploading}
                              onChange={(e) => {
                                const file = e.target.files && e.target.files[0];
                                if (file) {
                                  handleQuestionImageUpload(file, true);
                                }
                              }}
                            />
                          </label>
                          {editingQuiz.question_image_url && (
                            <span className="text-[11px] text-green-600 truncate max-w-[140px]">
                              Image selected
                            </span>
                          )}
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Image URL (question_image_url)
                        </label>
                        <input
                          type="text"
                          value={editingQuiz.question_image_url || ''}
                          onChange={(e) =>
                            setEditingQuiz({
                              ...editingQuiz,
                              question_image_url: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none"
                          placeholder="Paste image URL or use uploader above"
                        />
                      </div>
                      {editingQuiz.question_image_url && (
                        <div className="mt-1">
                          <img
                            src={editingQuiz.question_image_url}
                            alt="Question"
                            className="max-h-32 rounded-md border border-gray-200 object-contain"
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Difficulty Level */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Difficulty Level
                  </label>
                  <div className="relative custom-dropdown-container">
                    <button
                      type="button"
                      onClick={() => setActiveDropdown(activeDropdown === 'editDifficulty' ? null : 'editDifficulty')}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none flex items-center justify-between text-left bg-white"
                    >
                      <span className="truncate capitalize">
                        {editingQuiz.difficulty_level || 'medium'}
                      </span>
                      <HiChevronDown className={`w-4 h-4 transition-transform duration-300 ease-in-out ${activeDropdown === 'editDifficulty' ? 'rotate-180' : ''}`} />
                    </button>
                    {activeDropdown === 'editDifficulty' && (
                      <div className="absolute z-[60] mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden py-1">
                        {['easy', 'medium', 'hard'].map((level) => (
                          <button
                            key={level}
                            type="button"
                            onClick={() => {
                              setEditingQuiz({ ...editingQuiz, difficulty_level: level });
                              setActiveDropdown(null);
                            }}
                            className={`w-full px-4 py-2 text-sm text-left hover:bg-gray-100 transition-colors capitalize ${(editingQuiz.difficulty_level || 'medium') === level ? 'bg-gray-100 text-black font-semibold' : 'text-gray-700'}`}
                          >
                            {level}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Options - Only show for text_text, audio_text, audio_audio and image_text types */}
                {(editingQuiz.question_type === 'text_text' ||
                  editingQuiz.question_type === 'audio_text' ||
                  editingQuiz.question_type === 'audio_audio' ||
                  editingQuiz.question_type === 'image_text') && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-xs font-medium text-gray-700">
                          Options <span className="text-red-500">*</span> <span className="text-gray-500 font-normal">(2-10 options)</span>
                        </label>
                        {editingQuiz.options.length < 10 && (
                          <button
                            type="button"
                            onClick={() => addOption(true)}
                            className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-brand hover:bg-brand/10 rounded-lg transition-colors"
                          >
                            <HiPlus className="w-4 h-4" />
                            Add Option
                          </button>
                        )}
                      </div>
                      <div className="space-y-2">
                        {editingQuiz.options.map((option, index) => (
                          <div key={option.id} className="flex items-start gap-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs font-medium text-gray-600">
                                  Option {index + 1}
                                  {index < 2 && <span className="text-red-500 ml-1">*</span>}
                                </span>
                              </div>
                              <input
                                type="text"
                                value={option.text}
                                onChange={(e) => updateOptionText(option.id, e.target.value, true)}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none"
                                placeholder={`Enter option ${index + 1}`}
                              />
                            </div>
                            {editingQuiz.options.length > 2 && (
                              <button
                                type="button"
                                onClick={() => removeOption(option.id, true)}
                                className="mt-6 p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                title="Remove option"
                              >
                                <HiX className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                {/* Match Pairs - Only show for match_pairs type */}
                {editingQuiz.question_type === 'match_pairs' && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-xs font-medium text-gray-700">
                        Match Pairs <span className="text-red-500">*</span>
                      </label>
                      <button
                        type="button"
                        onClick={() => addMatchPair(true)}
                        className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-brand hover:bg-brand/10 rounded-lg transition-colors"
                      >
                        <HiPlus className="w-4 h-4" />
                        Add Pair
                      </button>
                    </div>
                    <div className="space-y-3">
                      {editingQuiz.match_pairs.map((pair, index) => (
                        <div key={pair.id} className="border border-gray-200 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-medium text-gray-600">
                              Pair {index + 1} <span className="text-red-500">*</span>
                            </span>
                            {editingQuiz.match_pairs.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeMatchPair(pair.id, true)}
                                className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors"
                                title="Remove pair"
                              >
                                <HiX className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Left Item</label>
                              <input
                                type="text"
                                value={pair.left_item}
                                onChange={(e) => updateMatchPair(pair.id, 'left_item', e.target.value, true)}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none"
                                placeholder="e.g., France"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Right Item</label>
                              <input
                                type="text"
                                value={pair.right_item}
                                onChange={(e) => updateMatchPair(pair.id, 'right_item', e.target.value, true)}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none"
                                placeholder="e.g., Paris"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Reorder Text - Only show for reorder_text type */}
                {editingQuiz.question_type === 'reorder_text' && (
                  <div className="mb-2">
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-xs font-medium text-gray-700">
                        Items (correct order) <span className="text-red-500">*</span> <span className="text-gray-500 font-normal">(drag to reorder)</span>
                      </label>
                      <button
                        type="button"
                        onClick={() => addReorderItem(true)}
                        className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-brand hover:bg-brand/10 rounded-lg transition-colors"
                      >
                        <HiPlus className="w-4 h-4" />
                        Add Item
                      </button>
                    </div>
                    <DndContext
                      modifiers={[restrictToVerticalAxis]}
                      onDragEnd={({ active, over }) => {
                        if (over && active.id !== over.id) {
                          const items = editingQuiz.reorder_correct_order || [];
                          const oldIdx = items.findIndex(item => item.id === active.id);
                          const newIdx = items.findIndex(item => item.id === over.id);
                          if (oldIdx !== -1 && newIdx !== -1) {
                            setEditingQuiz({ ...editingQuiz, reorder_correct_order: arrayMove(items, oldIdx, newIdx) });
                          }
                        }
                      }}
                    >
                      <SortableContext items={(editingQuiz.reorder_correct_order || []).map(item => item.id)}>
                        <div className="space-y-2">
                          {(editingQuiz.reorder_correct_order || []).map((item, index) => (
                            <SortableReorderRow
                              key={item.id}
                              id={item.id}
                              index={index}
                              value={item.text}
                              onChange={(val) => updateReorderItem(item.id, val, true)}
                              onRemove={(editingQuiz.reorder_correct_order || []).length > 2 ? () => removeReorderItem(item.id, true) : null}
                            />
                          ))}
                        </div>
                      </SortableContext>
                    </DndContext>
                    {editErrors.reorder_correct_order && (
                      <p className="mt-1 text-xs text-red-500">{editErrors.reorder_correct_order}</p>
                    )}
                  </div>
                )}

                {/* Fill in the Blank - Only show for fill_blank_text type */}
                {editingQuiz.question_type === 'fill_blank_text' && (
                  <div className="mb-2">
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-xs font-medium text-gray-700">
                        Segments <span className="text-red-500">*</span>
                      </label>
                      <button
                        type="button"
                        onClick={() => addFillBlankSegment(true)}
                        className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-brand hover:bg-brand/10 rounded-lg transition-colors"
                      >
                        + Add Segment
                      </button>
                    </div>
                    <p className="text-xs text-gray-400 mb-2">Blank segments = user must fill in; Static segments = shown as text</p>
                    <div className="space-y-2">
                      {(editingQuiz.fill_blank_correct_order || []).map((seg, index) => (
                        <div key={seg.id} className="flex items-center gap-2">
                          <input
                            type="text"
                            value={seg.title}
                            onChange={(e) => updateFillBlankSegment(seg.id, 'title', e.target.value, true)}
                            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none"
                            placeholder={seg.isBlank ? 'Answer text (hidden from user)' : 'Static text'}
                          />
                          <label className="flex items-center gap-1 text-xs text-gray-600 flex-shrink-0 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={seg.isBlank}
                              onChange={(e) => updateFillBlankSegment(seg.id, 'isBlank', e.target.checked, true)}
                              className="w-3 h-3 accent-brand"
                            />
                            Is Blank?
                          </label>
                          {(editingQuiz.fill_blank_correct_order || []).length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeFillBlankSegment(seg.id, true)}
                              className="p-2 text-brand hover:bg-brand/10 rounded-lg transition-colors flex-shrink-0"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    {editErrors.fill_blank_correct_order && (
                      <p className="mt-1 text-xs text-red-500">{editErrors.fill_blank_correct_order}</p>
                    )}
                  </div>
                )}

                {/* Correct answer & points */}
                <div
                  className={`grid grid-cols-1 ${editingQuiz.question_type === 'text_text' ||
                      editingQuiz.question_type === 'audio_text' ||
                      editingQuiz.question_type === 'audio_audio' ||
                      editingQuiz.question_type === 'image_text'
                      ? 'md:grid-cols-2'
                      : ''
                    } gap-4`}
                >
                  {(editingQuiz.question_type === 'text_text' ||
                    editingQuiz.question_type === 'audio_text' ||
                    editingQuiz.question_type === 'audio_audio' ||
                    editingQuiz.question_type === 'image_text') && (
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Correct Answer <span className="text-red-500">*</span>
                        </label>
                        <div className="relative custom-dropdown-container">
                          <button
                            type="button"
                            onClick={() => setActiveDropdown(activeDropdown === 'editCorrect' ? null : 'editCorrect')}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none flex items-center justify-between text-left bg-white"
                          >
                            <span className="truncate">
                              {editingQuiz.correct_answer_index !== undefined && editingQuiz.correct_answer_index >= 0 && editingQuiz.options[editingQuiz.correct_answer_index]
                                ? `Option ${editingQuiz.correct_answer_index + 1}: ${editingQuiz.options[editingQuiz.correct_answer_index].text || '(Empty)'}`
                                : 'Select Answer'}
                            </span>
                            <HiChevronDown className={`w-4 h-4  transition-transform duration-300 ease-in-out ${activeDropdown === 'editCorrect' ? 'rotate-180' : ''}`} />
                          </button>

                          {activeDropdown === 'editCorrect' && (
                            <div className="absolute z-[60] mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden py-1 max-h-60 overflow-y-auto">
                              {editingQuiz.options.map((option, index) => (
                                <button
                                  key={option.id}
                                  type="button"
                                  onClick={() => {
                                    setEditingQuiz({ ...editingQuiz, correct_answer_index: index });
                                    setActiveDropdown(null);
                                  }}
                                  className={`w-full px-4 py-2 text-sm text-left hover:bg-gray-100 transition-colors ${editingQuiz.correct_answer_index === index ? 'bg-gray-100 text-black font-semibold' : 'text-gray-700'
                                    }`}
                                >
                                  Option {index + 1}: {option.text || '(Empty)'}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      points <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      maxLength={6}
                      value={editingQuiz.points}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value && !isNaN(parseInt(value)) && parseInt(value) > 999999) {
                          setEditErrors({ ...editErrors, points: 'points cannot exceed 6 digits ' });
                          return;
                        }
                        if (value && value.length > 6) {
                          setEditErrors({ ...editErrors, points: 'points cannot exceed 6 digits' });
                          return;
                        }
                        setEditingQuiz({ ...editingQuiz, points: value });
                      }}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none"
                      placeholder="Enter points"
                    />
                    {editErrors.points && (
                      <p className="mt-1 text-xs text-red-500">
                        {editErrors.points}
                      </p>
                    )}
                  </div>
                </div>

                {/* Order */}
                {/* <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Order
                  </label>
                  <input
                    type="number"
                    value={editingQuiz.order}
                    onChange={(e) =>
                      setEditingQuiz({
                        ...editingQuiz,
                        order: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none"
                    placeholder="Display order"
                  />
                </div> */}

                {/* Explanation */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Explanation
                  </label>
                  <textarea
                    value={editingQuiz.explanation}
                    onChange={(e) =>
                      setEditingQuiz({
                        ...editingQuiz,
                        explanation: e.target.value,
                      })
                    }
                    rows={1}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none resize-none"
                    placeholder="Optional explanation for the answer"
                  />
                </div>
              </div>
              <div className="px-6 py-4 flex justify-end gap-3 border-t border-gray-200 flex-shrink-0">
                <button
                  onClick={() => setShowEditModal(false)}
                  disabled={editing}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditSubmit}
                  disabled={editing}
                  className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {editing ? (
                    <>
                      <svg
                        className="animate-spin h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Updating...
                    </>
                  ) : (
                    'Update Quiz'
                  )}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Delete Confirmation Modal */}
      {
        showDeleteModal && quizToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-xl font-bold text-gray-800">Delete Quiz</h2>
              </div>
              <div className="px-6 py-4">
                <p className="text-gray-700">
                  This hides the question from the app. Options and match pairs stay in the database.
                </p>
              </div>
              <div className="px-6 py-4 flex justify-end gap-3">
                <button
                  onClick={cancelDelete}
                  disabled={deleting}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={deleting}
                  className="px-4 py-2 text-sm font-medium text-white bg-[#F76464] rounded-lg  transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {deleting ? (
                    <>
                      <svg
                        className="animate-spin h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Deleting...
                    </>
                  ) : (
                    'Delete'
                  )}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Upload JSON Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-800">Upload Quiz via JSON</h2>
            </div>
            <div className="px-6 py-6 space-y-4">
              <p className="text-sm text-gray-600">
                Upload a JSON file containing your learning quiz. Ensure the file follows the required format for successful processing.
              </p>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-medium text-gray-700">Select JSON File</label>
                <input
                  type="file"
                  accept=".json"
                  onChange={(e) => setSelectedFile(e.target.files[0])}
                  className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-brand/10 file:text-brand hover:file:bg-brand/20 cursor-pointer"
                />
              </div>
            </div>
            <div className="px-6 py-4 flex justify-end gap-3 border-t border-gray-200">
              <button onClick={() => { setShowUploadModal(false); setSelectedFile(null); }} disabled={uploadingCsv} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleUploadCSV} disabled={uploadingCsv || !selectedFile} className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center gap-2">
                {uploadingCsv ? (<><svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>Uploading...</>) : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Result Modal */}
      {showUploadResultModal && (
        <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full overflow-hidden">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-800">JSON Upload Summary</h2>
              <button onClick={() => setShowUploadResultModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="border rounded-xl overflow-hidden">
                <button onClick={() => setOpenSection(openSection === 'success' ? null : 'success')} className="w-full flex items-center justify-between px-4 py-3 bg-green-50 hover:bg-green-100 transition">
                  <span className="font-semibold text-green-700">Successfully Added ({uploadResult.successful.length})</span>
                  <HiChevronDown className={`w-5 h-5 text-green-700 ${openSection === 'success' ? 'rotate-180' : ''}`} />
                </button>
                <div className={`px-4 bg-white overflow-hidden overflow-y-auto transition-all ${openSection === 'success' && uploadResult.successful.length > 0 ? 'max-h-40 opacity-100 py-3' : 'max-h-0 opacity-0 py-0'}`}>
                  <ul className="text-sm text-green-700 space-y-2">
                    {uploadResult.successful.map((item, i) => (
                      <li key={i} className="flex gap-2">
                        <span className="font-semibold whitespace-nowrap">Module {item.module ?? i + 1}:</span>
                        <span className="text-gray-700">
                          {item.kind === 'hierarchy_only' ? (
                            <div>{item.message}</div>
                          ) : (
                            <>
                              {item.unit != null && <div>Unit: {item.unit}{item.unit_name ? ` — ${item.unit_name}` : ''}</div>}
                              {item.lesson != null && <div>Lesson: {item.lesson}{item.lesson_name ? ` — ${item.lesson_name}` : ''}</div>}
                              {item.question != null && <div>Question: {item.question}{item.question_text ? ` — ${item.question_text}` : ''}{item.action ? ` (${item.action})` : ''}</div>}
                            </>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="border rounded-xl overflow-hidden">
                <button onClick={() => setOpenSection(openSection === 'skipped' ? null : 'skipped')} className="w-full flex items-center justify-between px-4 py-3 bg-yellow-50 hover:bg-yellow-100 transition">
                  <span className="font-semibold text-yellow-700">Skipped Records ({uploadResult.skipped.length})</span>
                  <HiChevronDown className={`w-5 h-5 text-yellow-700 ${openSection === 'skipped' ? 'rotate-180' : ''}`} />
                </button>
                <div className={`px-4 bg-white overflow-hidden overflow-y-auto transition-all ${openSection === 'skipped' && uploadResult.skipped.length > 0 ? 'max-h-40 opacity-100 py-3' : 'max-h-0 opacity-0 py-0'}`}>
                  <ul className="text-sm text-yellow-700 space-y-2">
                    {uploadResult.skipped.map((item, i) => (
                      <li key={i} className="flex gap-2">
                        <span className="font-semibold whitespace-nowrap">Row {item.row}:</span>
                        <span className="text-gray-700">
                          <div>module: {item.module}- {item.module_name}</div>
                          <div>Unit: {item.unit}{item.unit_name ? ` — ${item.unit_name}` : ''}</div>
                          <div>Lesson: {item.lesson}- {item.lesson_name}</div>
                          <div>Content: {item.content}- {item.content_name}</div>
                          <div>Question: {item.question}- {item.reason}</div>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              {uploadResult.error.length > 0 && (
                <div className="border border-red-200 rounded-xl overflow-hidden bg-red-50">
                  <button onClick={() => setOpenSection(openSection === 'error' ? null : 'error')} className="w-full flex items-center justify-between px-4 py-3 hover:bg-red-100 transition">
                    <span className="font-semibold text-red-700">Errors ({uploadResult.error.length})</span>
                    <HiChevronDown className={`w-5 h-5 text-red-700 ${openSection === 'error' ? 'rotate-180' : ''}`} />
                  </button>
                  <ul className={`px-4 text-sm text-red-700 space-y-2 overflow-hidden list-disc list-inside transition-all overflow-y-auto ${openSection === 'error' ? 'max-h-40 opacity-100 py-3' : 'max-h-0 opacity-0 py-0'}`}>
                    {uploadResult.error.map((item, i) => (
                      <li key={i}>Row {item.row} — {item.error || item.reason || item.message || 'Unknown server error'}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t flex justify-end">
              <button onClick={() => { setShowUploadResultModal(false); setOpenSection(null); }} className="px-5 py-2 text-sm font-semibold text-white bg-brand rounded-lg hover:opacity-90">Done</button>
            </div>
          </div>
        </div>
      )}

      {/* Reorder Modal */}
      {showReorderModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-hidden">
          <div className="bg-[#ffffff] w-full max-w-lg rounded-2xl md:ml-0 lg:ml-8 xl:ml-0 p-6 flex flex-col max-h-[90vh] overflow-hidden overflow-x-hidden">
            {/* Header */}
            <div className="flex justify-between items-center mb-4 flex-shrink-0">
              <h2 className="text-black/60 text-lg font-semibold">
                Reorder Quiz
              </h2>
              {/* <button
                onClick={() => setShowReorderModal(false)}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none"
              >
                ✕
              </button> */}
            </div>

            <p className="text-sm text-gray-400 mb-4 flex-shrink-0">
              Drag and drop quiz to reorder them. Click <b>Save Order</b> to apply.
            </p>

            {/* Drag List */}
            <DndContext
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis]}
              onDragStart={() => {
                // Prevent horizontal scrolling during drag
                document.body.style.overflowX = 'hidden';
              }}
              onDragEnd={(event) => {
                // Restore scrolling after drag
                document.body.style.overflowX = '';

                const { active, over } = event;
                if (!over || active.id === over.id) return;

                setReorderQuizzes((prev) => {
                  const oldIndex = prev.findIndex(
                    (q) => (q.id || q.question_id || q._id) === active.id
                  );
                  const newIndex = prev.findIndex(
                    (q) => (q.id || q.question_id || q._id) === over.id
                  );
                  return arrayMove(prev, oldIndex, newIndex);
                });
              }}
              onDragCancel={() => {
                // Restore scrolling if drag is cancelled
                document.body.style.overflowX = '';
              }}
            >
              <SortableContext
                items={reorderQuizzes.map(
                  (q) => q.id || q.question_id || q._id
                )}
                strategy={verticalListSortingStrategy}
              >
                {/* Calculate height: each item is ~72px (p-4 = 16px top/bottom + content ~40px) + gap-3 (12px) = ~84px per item, 5 items = ~420px */}
                <div className="space-y-3 overflow-y-auto overflow-x-hidden pr-2 flex-1 min-h-0" style={{ maxHeight: '420px' }}>
                  {reorderQuizzes.map((quiz, index) => (
                    <SortableQuizItem
                      key={quiz.id || quiz.question_id || quiz._id}
                      quiz={quiz}
                      index={index}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>

            {/* Footer */}
            <div className="flex justify-end gap-3 mt-6 flex-shrink-0">
              <button
                type="button"
                onClick={() => setShowReorderModal(false)}
                disabled={reorderSaveLoading}
                className="px-4 py-1 rounded-lg border border-gray-600 text-gray-500 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveQuizOrder}
                disabled={reorderSaveLoading}
                className="px-4 py-2 rounded-lg bg-brand text-white hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
              >
                {reorderSaveLoading ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Saving...
                  </>
                ) : (
                  'Save Order'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Quiz;

